import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "../../lib/convexApi";

// One row of api.mcp.accessLog.queries.recent (the mcpAccessLog table).
// Kept loose for the same reason ConnectAgentsTab does — the convex
// _generated proxy types these as `any` until `npx convex dev` regenerates.
interface AccessRow {
  _id: string;
  tokenId?: string | null;
  userId: string;
  tool: string;
  ok: boolean;
  durationMs: number;
  errorCode?: string | null;
  at: number;
}

interface TokenRow {
  _id: string;
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const NO_TOKEN = "—";

/**
 * ConnectionHealthPanel — recent MCP tool-call health for the org.
 *
 * Streams live from Convex (`api.mcp.accessLog.queries.recent`) and derives
 * the rolling-24h count, per-token usage, and recent errors client-side.
 * Replaces the deprecated `getMcpHealth()` REST stub (which only threw),
 * so the panel reacts to new tool calls without a manual poll loop.
 */
export function ConnectionHealthPanel() {
  const rows = useQuery((api as any).mcp.accessLog.queries.recent, { limit: 500 }) as
    | AccessRow[]
    | undefined;
  const tokens = useQuery((api as any).mcp.tokens.queries.list, {}) as TokenRow[] | undefined;

  // Wait for BOTH queries: if rows resolve before tokens, the table would
  // briefly render raw token IDs and then flash to their labels once the
  // token list lands. Gate the panel on both to avoid that flicker.
  const loading = rows === undefined || tokens === undefined;

  const { rollingCallsLast24h, perTokenUsage, lastErrors } = useMemo(() => {
    const list = rows ?? [];
    const labelById = new Map((tokens ?? []).map((t) => [t._id, t.label] as const));
    const cutoff = Date.now() - DAY_MS;

    const byToken = new Map<
      string,
      { tokenId: string; label: string; callCount: number; lastUsedAt: number | null }
    >();
    for (const r of list) {
      const id = r.tokenId ?? NO_TOKEN;
      const entry = byToken.get(id) ?? {
        tokenId: id,
        label: labelById.get(id) ?? id,
        callCount: 0,
        lastUsedAt: null,
      };
      entry.callCount += 1;
      entry.lastUsedAt = entry.lastUsedAt === null ? r.at : Math.max(entry.lastUsedAt, r.at);
      byToken.set(id, entry);
    }

    return {
      rollingCallsLast24h: list.filter((r) => r.at >= cutoff).length,
      perTokenUsage: Array.from(byToken.values()).sort((a, b) => b.callCount - a.callCount),
      lastErrors: list
        .filter((r) => !r.ok)
        .slice(0, 20)
        .map((r) => ({
          tokenId: r.tokenId ?? NO_TOKEN,
          timestamp: r.at,
          errorCode: r.errorCode ?? "error",
          tool: r.tool,
        })),
    };
  }, [rows, tokens]);

  return (
    <section className="ac-tab-section" aria-label="Connection health">
      <h3>Connection health</h3>
      {loading ? (
        <p className="ac-loading">Loading health…</p>
      ) : (
        <>
          <p className="ac-tab-subtitle">
            <strong>{rollingCallsLast24h}</strong> tool calls in the last 24 hours.
          </p>
          {perTokenUsage.length > 0 ? (
            <table className="ac-vault-table" aria-label="Per-token usage">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Calls</th>
                  <th>Last used</th>
                </tr>
              </thead>
              <tbody>
                {perTokenUsage.map((row) => (
                  <tr key={row.tokenId} className="ac-vault-row">
                    <td className="ac-vault-row__name">{row.label}</td>
                    <td>{row.callCount}</td>
                    <td>{row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="ac-empty-state">
              <p>No MCP activity yet. Install an agent to start seeing requests here.</p>
            </div>
          )}
          {lastErrors.length > 0 && (
            <>
              <h4>Last errors</h4>
              <table className="ac-vault-table" aria-label="Last errors">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Token</th>
                    <th>Tool</th>
                    <th>Code</th>
                  </tr>
                </thead>
                <tbody>
                  {lastErrors.map((row, idx) => (
                    <tr key={`${row.tokenId}-${idx}`} className="ac-vault-row">
                      <td>{new Date(row.timestamp).toLocaleString()}</td>
                      <td>{row.tokenId}</td>
                      <td>{row.tool}</td>
                      <td>
                        <code>{row.errorCode}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </section>
  );
}
